import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth.jsx";
import ClientBreadcrumb from "../../components/ClientBreadcrumb.jsx";

const STAGE = { UPLOAD: "upload", PROCESSING: "processing", REVIEW: "review", DONE: "done" };
const STAGE_ORDER = [STAGE.UPLOAD, STAGE.PROCESSING, STAGE.REVIEW, STAGE.DONE];
const STAGE_LABELS = { upload: "Upload", processing: "Processing", review: "Review", done: "Done" };

const DOC_TYPES = [
  { value: "family_letter",    label: "Family letter" },
  { value: "shift_notes",      label: "Shift notes" },
  { value: "behavioral_plan",  label: "Behavioral plan" },
  { value: "health_plan",      label: "Health plan" },
  { value: "parent_interview", label: "Parent interview" },
  { value: "other",            label: "Other" },
];
const DOC_LABEL = Object.fromEntries([["isp","ISP"], ...DOC_TYPES.map(t => [t.value, t.label])]);

const SOURCE_COLORS = {
  isp:              { bg: "var(--color-accent-subtle)",  text: "var(--color-accent)" },
  family_letter:    { bg: "rgba(139,92,246,0.08)",       text: "#7c3aed" },
  shift_notes:      { bg: "rgba(16,185,129,0.08)",       text: "#059669" },
  behavioral_plan:  { bg: "rgba(245,158,11,0.08)",       text: "#d97706" },
  health_plan:      { bg: "rgba(236,72,153,0.08)",       text: "#db2777" },
  parent_interview: { bg: "rgba(6,182,212,0.08)",        text: "#0891b2" },
  other:            { bg: "var(--color-bg-inset)",       text: "var(--color-text-tertiary)" },
};

function SourceBadge({ source }) {
  const colors = SOURCE_COLORS[source] || SOURCE_COLORS.other;
  return (
    <span style={{ fontSize: 10, fontWeight: "var(--weight-semibold)", padding: "2px 6px",
      borderRadius: "var(--radius-full)", background: colors.bg, color: colors.text,
      whiteSpace: "nowrap", flexShrink: 0, textTransform: "capitalize" }}>
      {DOC_LABEL[source] || source}
    </span>
  );
}

// ── Step indicator ─────────────────────────────────────────────────────────

function StepIndicator({ stage }) {
  const idx = STAGE_ORDER.indexOf(stage);
  return (
    <div style={s.steps}>
      {STAGE_ORDER.map((st, i) => (
        <React.Fragment key={st}>
          <div style={s.step}>
            <div style={{ ...s.dot, background: i <= idx ? "var(--color-accent)" : "var(--color-bg-inset)",
              color: i <= idx ? "#fff" : "var(--color-text-tertiary)" }}>
              {i < idx ? "✓" : i + 1}
            </div>
            <span style={{ ...s.dotLabel, color: i === idx ? "var(--color-text-primary)" : "var(--color-text-tertiary)" }}>
              {STAGE_LABELS[st]}
            </span>
          </div>
          {i < STAGE_ORDER.length - 1 &&
            <div style={{ ...s.line, background: i < idx ? "var(--color-accent)" : "var(--color-bg-inset)" }} />}
        </React.Fragment>
      ))}
    </div>
  );
}

// ── Upload phase ───────────────────────────────────────────────────────────


// ── Document preview modal (Quick Start: preview staged documents) ────────────
// Shows extracted text (what the system will actually ingest) + a link to
// open the original file — so the coordinator can decide keep/delete before
// clicking Process. raw_text already exists at upload; this is read+display.

export function DocumentPreviewModal({ clientId, doc, onClose }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetch(`/api/clients/${clientId}/documents/${doc.id}/preview`)
      .then(r => { if (!r.ok) throw new Error("Could not load preview"); return r.json(); })
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [clientId, doc.id]);

  return (
    <div style={pv.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={pv.box}>
        <div style={pv.header}>
          <div style={pv.headerLeft}>
            <SourceBadge source={doc.document_type} />
            <p style={pv.filename}>{doc.filename}</p>
          </div>
          <button style={pv.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div style={pv.body}>
          {loading && <p style={pv.hint}>Loading preview…</p>}
          {error && <p style={pv.error}>{error}</p>}
          {data && !data.has_text && (
            <p style={pv.emptyHint}>
              No extracted text available for this document — it may be a scanned
              image, an unsupported format, or extraction hasn't completed. Open
              the original file below to check its contents.
            </p>
          )}
          {data?.has_text && (
            <pre style={pv.text}>{data.raw_text}</pre>
          )}
        </div>

        <div style={pv.footer}>
          <a
            href={`/api/clients/${clientId}/documents/${doc.id}/file`}
            target="_blank" rel="noopener noreferrer"
            style={pv.openOriginalBtn}
          >
            Open original file ↗
          </a>
          <button style={pv.closeFooterBtn} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function UploadPhase({ clientId, onProcess }) {
  const [isp, setIsp]               = useState(null);   // { id, filename }
  const [extras, setExtras]         = useState([]);     // [{ id, filename, document_type }]
  const [extraType, setExtraType]   = useState(DOC_TYPES[0].value);
  const [uploading, setUploading]   = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError]           = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);   // { id, filename, document_type } | null
  const ispRef  = useRef();
  const addRef  = useRef();

  // Pre-populate from existing docs (e.g. after "Clear content, keep docs" reset)
  useEffect(() => {
    fetch(`/api/clients/${clientId}/documents`)
      .then(r => r.json())
      .then(docs => {
        if (!Array.isArray(docs)) return;
        const live = docs.filter(d => d.status !== "superseded");
        const existIsp = live.find(d => d.document_type === "isp");
        const existOther = live.filter(d => d.document_type !== "isp");
        if (existIsp) setIsp({ id: existIsp.id, filename: existIsp.filename });
        if (existOther.length) setExtras(existOther.map(d => ({ id: d.id, filename: d.filename, document_type: d.document_type })));
      })
      .catch(() => {});
  }, [clientId]);

  async function upload(file, type) {
    setUploading(true);
    setError(null);
    const form = new FormData();
    form.append("file", file);
    form.append("document_type", type);
    const endpoint = type === "isp"
      ? `/api/clients/${clientId}/documents`
      : `/api/clients/${clientId}/documents/additional`;
    try {
      const res  = await fetch(endpoint, { method: "POST", body: form });
      const data = await res.json();
      if (!res.ok) { setError(data.error || "Upload failed"); return; }
      if (type === "isp") setIsp({ id: data.id, filename: file.name });
      else setExtras(prev => [...prev, { id: data.id, filename: file.name, document_type: type }]);
    } catch { setError("Upload failed — please try again."); }
    finally { setUploading(false); }
  }

  async function deleteDoc(docId, isIsp) {
    await fetch(`/api/clients/${clientId}/documents/${docId}`, { method: "DELETE" }).catch(() => {});
    if (isIsp) setIsp(null);
    else setExtras(prev => prev.filter(d => d.id !== docId));
  }

  async function handleProcess() {
    if (!isp || processing) return;
    setProcessing(true);
    setError(null);
    try {
      const res  = await fetch(`/api/clients/${clientId}/documents/process-all`, { method: "POST" });
      const data = await res.json();
      if (!res.ok) { setError(data.error || "Processing failed"); setProcessing(false); return; }
      onProcess(data.document_id);
    } catch { setError("Could not start processing — please try again."); setProcessing(false); }
  }

  const canProcess = !!isp && !uploading && !processing;

  return (
    <div style={s.card}>
      <h2 style={s.heading}>Upload documents</h2>
      <p style={s.body}>
        Upload the ISP (required) and any supporting documents you have — shift notes, a family letter, behavior plans. When everything is ready, click <strong>Process</strong>. We'll extract from all of them at once, with the ISP as the authoritative source.
      </p>

      {error && <p style={s.error}>{error}</p>}

      {/* ISP */}
      <div style={up.section}>
        <div style={up.row}>
          <span style={up.sectionTitle}>ISP</span>
          <span style={up.reqBadge}>Required</span>
        </div>
        {isp ? (
          <div style={up.docRow}>
            <SourceBadge source="isp" />
            <button style={up.filenameBtn} onClick={() => setPreviewDoc({ id: isp.id, filename: isp.filename, document_type: "isp" })}>
              {isp.filename}
            </button>
            <button style={up.del} onClick={() => deleteDoc(isp.id, true)}>×</button>
          </div>
        ) : (
          <div style={up.zone} onDragOver={e => e.preventDefault()}
            onDrop={e => { e.preventDefault(); upload(e.dataTransfer.files[0], "isp"); }}
            onClick={() => ispRef.current.click()}>
            <input ref={ispRef} type="file" accept=".docx,.txt,.pdf" style={{ display:"none" }}
              onChange={e => upload(e.target.files[0], "isp")} />
            <p style={up.zoneText}>Drop ISP here, or click to browse</p>
            <p style={up.zoneHint}>.docx, .txt, or .pdf</p>
          </div>
        )}
      </div>

      {/* Additional docs */}
      <div style={up.section}>
        <div style={up.row}>
          <span style={up.sectionTitle}>Additional documents</span>
          <span style={up.optBadge}>Optional</span>
        </div>
        {extras.length > 0 && (
          <div style={{ display:"flex", flexDirection:"column", gap:"var(--space-2)", marginBottom:"var(--space-3)" }}>
            {extras.map(doc => (
              <div key={doc.id} style={up.docRow}>
                <SourceBadge source={doc.document_type} />
                <button style={up.filenameBtn} onClick={() => setPreviewDoc(doc)}>
                  {doc.filename}
                </button>
                <button style={up.del} onClick={() => deleteDoc(doc.id, false)}>×</button>
              </div>
            ))}
          </div>
        )}
        <div style={up.pills}>
          {DOC_TYPES.map(t => (
            <button key={t.value}
              style={{ ...up.pill, ...(extraType === t.value ? up.pillActive : {}) }}
              onClick={() => setExtraType(t.value)}>{t.label}</button>
          ))}
        </div>
        <div style={up.zoneSmall} onDragOver={e => e.preventDefault()}
          onDrop={e => { e.preventDefault(); upload(e.dataTransfer.files[0], extraType); }}
          onClick={() => addRef.current.click()}>
          <input ref={addRef} type="file" accept=".docx,.txt,.pdf" style={{ display:"none" }}
            multiple
            onChange={async e => { for (const f of e.target.files) await upload(f, extraType); }} />
          <p style={up.zoneHint}>Drop document here, or click to browse</p>
        </div>
      </div>

      <button style={{ ...s.btn, ...(canProcess ? {} : s.btnDisabled) }}
        disabled={!canProcess} onClick={handleProcess}>
        {processing ? "Starting…" : uploading ? "Uploading…" : "Process documents →"}
      </button>

      {previewDoc && (
        <DocumentPreviewModal clientId={clientId} doc={previewDoc} onClose={() => setPreviewDoc(null)} />
      )}
    </div>
  );
}

// ── Processing view ────────────────────────────────────────────────────────

// How long before the page stops promising and says processing may have
// stopped (Oct 24). A large ISP can genuinely take several minutes; a job that
// died with a deploy or restart never finishes, and the page used to wait on it
// forever. Exported for the test.
export const STALL_MS = 20 * 60 * 1000;
// The server beats every 20 s while it works (Oct 28); 90 s of silence means
// the work stopped, usually a server restart.
export const HEARTBEAT_STALE_S = 90;

/** Has background processing of these documents stopped? */
export function processingStopped(docs, ids) {
  return (docs || []).some(d => ids.includes(d.id)
    && (d.status === "uploaded" || d.status === "processing")
    && Number.isFinite(d.heartbeat_age_s) && d.heartbeat_age_s > HEARTBEAT_STALE_S);
}

function ProcessingView({ clientId, documentId, onReady, stallMs = STALL_MS }) {
  const [stalled,  setStalled]  = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [retryErr, setRetryErr] = useState(null);
  const [round,    setRound]    = useState(0);     // restarts the poll and the clock
  const [docId,    setDocId]    = useState(documentId);

  useEffect(() => {
    setStalled(false);
    const started = Date.now();
    const id = setInterval(async () => {
      try {
        const res  = await fetch(`/api/clients/${clientId}/documents`);
        const docs = await res.json();
        if (!Array.isArray(docs)) return;
        const isp     = docs.find(d => d.id === docId);
        const ispDone = isp?.status === "extracted" || isp?.status === "reviewed";
        // Also wait for any doc that's still actively processing
        const anyActive = docs.some(d => d.status === "processing");
        if (ispDone && !anyActive) { clearInterval(id); onReady(); return; }
        // No heartbeat for a while: the work stopped. Say so now, not in 20 minutes.
        if (processingStopped(docs, docs.map(d => d.id))) { setStalled(true); return; }
      } catch { /* retry */ }
      if (Date.now() - started > stallMs) setStalled(true);
    }, 3000);
    return () => clearInterval(id);
  }, [clientId, docId, onReady, round, stallMs]);

  async function tryAgain() {
    setRetrying(true); setRetryErr(null);
    try {
      const res  = await fetch(`/api/clients/${clientId}/documents/process-all`, { method: "POST" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Couldn't restart (${res.status})`);
      if (data.document_id) setDocId(data.document_id);
      setRound(r => r + 1);
    } catch (e) {
      setRetryErr(e.message);
    } finally {
      setRetrying(false);
    }
  }

  return (
    <div style={s.card}>
      {!stalled ? (
        <p style={s.processing}>Analyzing all documents — this can take a few minutes…</p>
      ) : (
        <div role="alert">
          <p style={s.processing}>
            Processing stopped before it finished — usually because the server
            restarted while it was working. Nothing was lost; start it again.
          </p>
          <div style={{ textAlign: "center" }}>
            <button style={{ ...s.btn, ...(retrying ? s.btnDisabled : {}) }} onClick={tryAgain} disabled={retrying}>
              {retrying ? "Starting…" : "Process again"}
            </button>
          </div>
          {retryErr && <p style={s.error}>{retryErr}</p>}
        </div>
      )}
    </div>
  );
}

// ── Review ─────────────────────────────────────────────────────────────────

function ProposalCard({ item, label, checked, onChange }) {
  return (
    <label style={{ ...rv.card, ...(checked ? rv.cardChecked : {}) }}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)}
        style={{ flexShrink:0, marginTop:2 }} />
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ display:"flex", alignItems:"center", gap:"var(--space-2)", flexWrap:"wrap", marginBottom:2 }}>
          <p style={rv.cardTitle}>{label}</p>
          {item.source && item.source !== "isp" && <SourceBadge source={item.source} />}
        </div>
        {item.description && <p style={rv.cardBody}>{item.description}</p>}
        {item.purpose     && <p style={rv.cardBody}>{item.purpose}</p>}
        {item.relationship && !item.description && <p style={rv.cardBody}>{item.relationship}</p>}
      </div>
    </label>
  );
}

function EntitySection({ heading, items, getLabel, selected, onToggle }) {
  if (!items?.length) return null;
  return (
    <div style={rv.section}>
      <p style={rv.sectionHead}>{heading} <span style={rv.count}>({items.length})</span></p>
      {items.map((item, i) => (
        <ProposalCard key={i} item={item} label={getLabel(item)}
          checked={selected.has(i)} onChange={v => onToggle(i, v)} />
      ))}
    </div>
  );
}

const SECTION_LABELS = {
  meet:"Meet", communicate:"Communication", sensory:"Sensory",
  passions:"Passions", day:"Daily Rhythm", working_on:"Working On",
  hard_times:"Hard Times", health:"Health", people:"People", community:"Community",
};

function SectionTextCard({ sectionKey, text, checked, onChange }) {
  const sources = text.sources || ["isp"];
  return (
    <label style={{ ...rv.card, ...(checked ? rv.cardChecked : {}), flexDirection:"column", alignItems:"flex-start", gap:"var(--space-2)" }}>
      <div style={{ display:"flex", alignItems:"center", gap:"var(--space-2)", width:"100%" }}>
        <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} />
        <span style={rv.cardTitle}>{SECTION_LABELS[sectionKey] || sectionKey}</span>
        <div style={{ display:"flex", gap:"var(--space-1)", marginLeft:"auto", flexWrap:"wrap" }}>
          {sources.map(src => <SourceBadge key={src} source={src} />)}
        </div>
      </div>
      {text.lead_paragraph && <p style={{ ...rv.cardBody, margin:0 }}>{text.lead_paragraph}</p>}
      {text.tile_teaser && (
        <p style={{ fontSize:"var(--text-xs)", color:"var(--color-text-tertiary)", margin:0 }}>
          Tile: {text.tile_teaser}
        </p>
      )}
    </label>
  );
}

function ContradictionsPanel({ contradictions }) {
  if (!contradictions?.length) return null;
  return (
    <div style={ct.panel}>
      <div style={ct.header}>
        <span style={ct.icon}>⚠</span>
        <span style={ct.title}>{contradictions.length} contradiction{contradictions.length !== 1 ? "s" : ""} found</span>
      </div>
      <p style={ct.sub}>The ISP is authoritative. Review each conflict and decide which version to keep — you can edit records in the section editors after approving.</p>
      {contradictions.map((c, i) => (
        <div key={i} style={ct.row}>
          <p style={ct.desc}>{c.description}</p>
          <div style={ct.cols}>
            <div style={ct.col}>
              <p style={ct.colLabel}>ISP (authoritative)</p>
              <p style={ct.colVal}>{c.isp_value}</p>
            </div>
            <div style={ct.colDivider}>vs.</div>
            <div style={ct.col}>
              <p style={ct.colLabel}>{DOC_LABEL[c.other_source] || c.other_source}</p>
              <p style={ct.colVal}>{c.other_value}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ReviewView({ clientId, documentId, onApproved }) {
  const [proposals, setProposals] = useState(null);
  const [selItems,    setItems]   = useState(new Set());
  const [selGoals,    setGoals]   = useState(new Set());
  const [selPeople,   setPeople]  = useState(new Set());
  const [selRoutines, setRoutines]= useState(new Set());
  const [selSections, setSections]= useState(new Set());
  const [approving, setApproving] = useState(false);
  const [approveError, setApproveError] = useState(null);

  const [extractError, setExtractError] = useState(null);

  useEffect(() => {
    fetch(`/api/clients/${clientId}/documents/${documentId}/proposals`)
      .then(r => r.json())
      .then(data => {
        const p = data.proposals;
        // A failed extraction leaves the document "extracted" with an `error`
        // and no proposals; this page used to render an empty review and an
        // "Approve 0 records + 0 sections" button (David, Oct 15). Say what
        // went wrong instead.
        const empty = !p || (!(p.items||[]).length && !(p.goals||[]).length && !(p.people||[]).length
                             && !(p.routines||[]).length && !Object.keys(p.section_texts||{}).length);
        if (data.error || empty) {
          setExtractError(data.error || "Extraction finished but produced nothing — the document may be empty or unreadable.");
        }
        setProposals(p);
        setItems(new Set((p?.items||[]).map((_,i)=>i)));
        setGoals(new Set((p?.goals||[]).map((_,i)=>i)));
        setPeople(new Set((p?.people||[]).map((_,i)=>i)));
        setRoutines(new Set((p?.routines||[]).map((_,i)=>i)));
        setSections(new Set(Object.keys(p?.section_texts||{})));
      })
      .catch(() => setExtractError("Could not load the extracted content."));
  }, [clientId, documentId]);

  function toggle(setter) {
    return (idx, checked) => setter(prev => {
      const next = new Set(prev);
      checked ? next.add(idx) : next.delete(idx);
      return next;
    });
  }

  // A failed approve used to fall through to the Done screen as "0 records
  // created" (David, Oct 14) — the error body was never read. Now any non-2xx
  // stays on this screen with the server's reason, and the selections intact.
  async function approve() {
    setApproving(true);
    setApproveError(null);
    try {
      const res  = await fetch(`/api/clients/${clientId}/documents/${documentId}/approve`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: [...selItems], goals: [...selGoals],
          people: [...selPeople], routines: [...selRoutines],
          section_keys: [...selSections],
        }),
      });
      let data = null;
      try { data = await res.json(); } catch { data = null; }
      if (!res.ok) {
        setApproveError(`Approve failed (${res.status})${data?.error ? `: ${data.error}` : ""}. Nothing was saved — your selections are still here.`);
        return;
      }
      onApproved(data?.created || {});
    } catch {
      setApproveError("Approve failed — could not reach the server. Nothing was saved.");
    } finally {
      setApproving(false);
    }
  }

  if (extractError) {
    return (
      <div style={s.card}>
        <h2 style={s.heading}>Extraction didn't produce anything to review</h2>
        <p style={s.error} role="alert">{extractError}</p>
        <p style={s.body}>
          Nothing has been changed. Go back to the upload step to check the documents, then process again.
          If this keeps happening, send the message above to support.
        </p>
        <button style={s.btn} onClick={() => window.location.assign(`/coordinator/clients/${clientId}/quick-start`)}>
          Back to documents
        </button>
      </div>
    );
  }
  if (!proposals) return <div style={s.card}><p style={s.processing}>Loading proposals…</p></div>;

  const totalEntities = selItems.size + selGoals.size + selPeople.size + selRoutines.size;
  const totalSections = selSections.size;
  const sectionEntries = Object.entries(proposals.section_texts || {});
  const multiSource = [
    ...(proposals.items||[]),
    ...(proposals.goals||[]),
    ...(proposals.people||[]),
    ...(proposals.routines||[]),
  ].some(e => e.source && e.source !== "isp");

  return (
    <div style={s.card}>
      <h2 style={s.heading}>Review extracted content</h2>
      <p style={s.body}>
        Uncheck anything that looks wrong before approving.
        {multiSource && " Source badges show which document contributed each item."}
      </p>

      <ContradictionsPanel contradictions={proposals.contradictions} />

      {/* Entities */}
      <EntitySection heading="Items" items={proposals.items}
        getLabel={i => i.title} selected={selItems} onToggle={toggle(setItems)} />
      <EntitySection heading="Goals" items={proposals.goals}
        getLabel={g => g.translated_text} selected={selGoals} onToggle={toggle(setGoals)} />
      <EntitySection heading="People" items={proposals.people}
        getLabel={p => p.display_name} selected={selPeople} onToggle={toggle(setPeople)} />
      <EntitySection heading="Routines" items={proposals.routines}
        getLabel={r => `${r.time_of_day} — ${r.title}`} selected={selRoutines} onToggle={toggle(setRoutines)} />

      {/* Section texts */}
      {sectionEntries.length > 0 && (
        <div style={rv.section}>
          <p style={rv.sectionHead}>
            Section prose <span style={rv.count}>({sectionEntries.length})</span>
            <span style={{ fontWeight:"normal", textTransform:"none", letterSpacing:0, marginLeft:"var(--space-2)", color:"var(--color-text-tertiary)" }}>
              — caregiver-facing text, composed from all sources
            </span>
          </p>
          {sectionEntries.map(([key, text]) => (
            <SectionTextCard key={key} sectionKey={key} text={text}
              checked={selSections.has(key)}
              onChange={v => setSections(prev => { const n=new Set(prev); v?n.add(key):n.delete(key); return n; })} />
          ))}
        </div>
      )}

      {approveError && <p style={s.error} role="alert">{approveError}</p>}
      <button style={{ ...s.btn, ...(totalEntities === 0 && totalSections === 0 ? s.btnDisabled : {}) }}
        onClick={approve} disabled={approving || (totalEntities === 0 && totalSections === 0)}>
        {approving ? "Saving…" : `Approve ${totalEntities} record${totalEntities!==1?"s":""} + ${totalSections} section${totalSections!==1?"s":""}`}
      </button>
    </div>
  );
}

// ── Done ───────────────────────────────────────────────────────────────────

function DoneView({ clientId, created }) {
  const navigate = useNavigate();
  const total = Object.values(created).reduce((a,b) => a+b, 0);
  return (
    <div style={s.doneWrap}>
      <p style={{ fontSize:48 }}>✓</p>
      <h2 style={s.doneHead}>Done — {total} records created</h2>
      <p style={{ fontSize:"var(--text-sm)", color:"var(--color-text-secondary)", textAlign:"center", maxWidth:420 }}>
        Records and section prose are saved as drafts. You can refine them in the section editors.
      </p>
      <div style={s.doneStats}>
        {Object.entries(created).filter(([,n])=>n>0).map(([k,n])=>(
          <span key={k} style={s.doneStat}>{n} {k}</span>
        ))}
      </div>
      <button style={s.btn} onClick={() => navigate(`/coordinator/clients/${clientId}/edit`)}>
        Review in section editors →
      </button>
    </div>
  );
}

// ── Main ───────────────────────────────────────────────────────────────────

export default function QuickStart() {
  const { clientId } = useParams();
  const [searchParams] = useSearchParams();
  const rerunDocId = searchParams.get("doc_id");
  const { user } = useAuth();
  const [clientName, setClientName] = useState("");
  useEffect(() => {
    fetch(`/api/clients/${clientId}`)
      .then(r => r.json())
      .then(d => setClientName(d.display_name || ""))
      .catch(() => {});
  }, [clientId]);

  const [stage, setStage]         = useState(rerunDocId ? STAGE.PROCESSING : STAGE.UPLOAD);
  const [documentId, setDocId]    = useState(rerunDocId || null);
  const [created, setCreated]     = useState(null);

  const onProcess  = (docId) => { setDocId(docId);   setStage(STAGE.PROCESSING); };
  const onReady    = useCallback(() => setStage(STAGE.REVIEW), []);
  const onApproved = (counts) => { setCreated(counts); setStage(STAGE.DONE); };

  return (
    <div style={s.page}>
      <div style={s.content}>
        <div style={s.topBar}>
          <ClientBreadcrumb
            clientName={clientName}
            role={user?.role}
            clientId={clientId}
            trail={[{ label: "Quick Start" }]}
          />
          <h1 style={s.title}>Quick Start</h1>
        </div>
        <StepIndicator stage={stage} />
        {stage === STAGE.UPLOAD     && <UploadPhase    clientId={clientId} onProcess={onProcess} />}
        {stage === STAGE.PROCESSING && <ProcessingView clientId={clientId} documentId={documentId} onReady={onReady} />}
        {stage === STAGE.REVIEW     && <ReviewView     clientId={clientId} documentId={documentId} onApproved={onApproved} />}
        {stage === STAGE.DONE       && <DoneView       clientId={clientId} created={created || {}} />}
      </div>
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────

const s = {
  page:       { minHeight:"100dvh", background:"var(--color-bg-subtle)", padding:"var(--space-6)" },
  content:    { maxWidth:660, margin:"0 auto", display:"flex", flexDirection:"column", gap:"var(--space-6)" },
  topBar:     { display:"flex", alignItems:"center", gap:"var(--space-4)" },
  back:       { fontSize:"var(--text-sm)", color:"var(--color-text-tertiary)", textDecoration:"none" },
  title:      { fontSize:"var(--text-2xl)", fontWeight:"var(--weight-bold)", color:"var(--color-text-primary)" },
  steps:      { display:"flex", alignItems:"center" },
  step:       { display:"flex", flexDirection:"column", alignItems:"center", gap:"var(--space-1)" },
  dot:        { width:28, height:28, borderRadius:"var(--radius-full)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"var(--text-xs)", fontWeight:"var(--weight-bold)" },
  dotLabel:   { fontSize:"var(--text-xs)", fontWeight:"var(--weight-medium)" },
  line:       { flex:1, height:2, margin:"0 var(--space-2)", marginBottom:"var(--space-4)" },
  card:       { background:"var(--color-surface)", border:"1px solid var(--color-border)", borderRadius:"var(--radius-xl)", padding:"var(--space-8)", display:"flex", flexDirection:"column", gap:"var(--space-5)" },
  heading:    { fontSize:"var(--text-xl)", fontWeight:"var(--weight-bold)", color:"var(--color-text-primary)" },
  body:       { fontSize:"var(--text-sm)", color:"var(--color-text-secondary)", lineHeight:"var(--leading-normal)" },
  processing: { fontSize:"var(--text-base)", color:"var(--color-text-secondary)", textAlign:"center", padding:"var(--space-6) 0" },
  error:      { color:"var(--color-critical)", fontSize:"var(--text-sm)" },
  btn:        { background:"var(--color-accent)", color:"var(--color-text-on-accent)", border:"none", borderRadius:"var(--radius-md)", padding:"var(--space-3) var(--space-6)", fontSize:"var(--text-base)", fontWeight:"var(--weight-semibold)", cursor:"pointer" },
  btnDisabled:{ opacity:0.5, cursor:"not-allowed" },
  doneWrap:   { display:"flex", flexDirection:"column", alignItems:"center", gap:"var(--space-5)", padding:"var(--space-4) 0" },
  doneHead:   { fontSize:"var(--text-2xl)", fontWeight:"var(--weight-bold)", color:"var(--color-text-primary)", textAlign:"center" },
  doneStats:  { display:"flex", gap:"var(--space-3)", flexWrap:"wrap", justifyContent:"center" },
  doneStat:   { fontSize:"var(--text-sm)", background:"var(--color-bg-subtle)", border:"1px solid var(--color-border)", borderRadius:"var(--radius-full)", padding:"3px var(--space-3)", color:"var(--color-text-secondary)" },
};

const up = {
  section:    { display:"flex", flexDirection:"column", gap:"var(--space-3)" },
  row:        { display:"flex", alignItems:"center", gap:"var(--space-2)" },
  sectionTitle:{ fontSize:"var(--text-sm)", fontWeight:"var(--weight-semibold)", color:"var(--color-text-primary)" },
  reqBadge:   { fontSize:10, fontWeight:"var(--weight-semibold)", textTransform:"uppercase", letterSpacing:"0.06em", color:"var(--color-critical)", background:"rgba(220,38,38,0.08)", borderRadius:"var(--radius-full)", padding:"2px var(--space-2)" },
  optBadge:   { fontSize:10, fontWeight:"var(--weight-semibold)", textTransform:"uppercase", letterSpacing:"0.06em", color:"var(--color-text-tertiary)", background:"var(--color-bg-inset)", borderRadius:"var(--radius-full)", padding:"2px var(--space-2)" },
  docRow:     { display:"flex", alignItems:"center", gap:"var(--space-3)", padding:"var(--space-2) var(--space-3)", background:"var(--color-bg-subtle)", border:"1px solid var(--color-border)", borderRadius:"var(--radius-md)" },
  filename:   { fontSize:"var(--text-sm)", color:"var(--color-text-primary)", flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" },
  filenameBtn:{ fontSize:"var(--text-sm)", color:"var(--color-accent)", background:"none", border:"none", padding:0, cursor:"pointer", flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", textAlign:"left", textDecoration:"underline", textUnderlineOffset:2 },
  del:        { background:"none", border:"none", color:"var(--color-text-tertiary)", cursor:"pointer", fontSize:"var(--text-lg)", padding:"0 var(--space-1)", flexShrink:0, lineHeight:1 },
  zone:       { background:"var(--color-bg-subtle)", border:"2px dashed var(--color-border)", borderRadius:"var(--radius-lg)", padding:"var(--space-8)", display:"flex", flexDirection:"column", alignItems:"center", gap:"var(--space-2)", cursor:"pointer" },
  zoneSmall:  { background:"var(--color-bg-subtle)", border:"1px dashed var(--color-border)", borderRadius:"var(--radius-md)", padding:"var(--space-4)", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer" },
  zoneText:   { fontSize:"var(--text-base)", color:"var(--color-text-secondary)", fontWeight:"var(--weight-medium)" },
  zoneHint:   { fontSize:"var(--text-xs)", color:"var(--color-text-tertiary)" },
  pills:      { display:"flex", gap:"var(--space-2)", flexWrap:"wrap" },
  pill:       { fontSize:"var(--text-xs)", padding:"3px var(--space-3)", borderRadius:"var(--radius-full)", border:"1px solid var(--color-border)", background:"var(--color-bg-base)", color:"var(--color-text-secondary)", cursor:"pointer" },
  pillActive: { background:"var(--color-accent)", color:"var(--color-text-on-accent)", borderColor:"var(--color-accent)" },
};

const rv = {
  section:    { display:"flex", flexDirection:"column", gap:"var(--space-2)" },
  sectionHead:{ fontSize:"var(--text-sm)", fontWeight:"var(--weight-semibold)", color:"var(--color-text-secondary)", textTransform:"uppercase", letterSpacing:"0.06em" },
  count:      { fontWeight:"normal", color:"var(--color-text-tertiary)" },
  card:       { display:"flex", gap:"var(--space-3)", padding:"var(--space-3) var(--space-4)", border:"1px solid var(--color-border)", borderRadius:"var(--radius-md)", cursor:"pointer", background:"var(--color-bg-base)" },
  cardChecked:{ borderColor:"var(--color-accent)", background:"var(--color-accent-subtle)" },
  cardTitle:  { fontSize:"var(--text-sm)", fontWeight:"var(--weight-medium)", color:"var(--color-text-primary)", margin:0 },
  cardBody:   { fontSize:"var(--text-xs)", color:"var(--color-text-secondary)", margin:0, marginTop:2 },
};

const ct = {
  panel:  { background:"rgba(245,158,11,0.06)", border:"1px solid rgba(245,158,11,0.25)", borderRadius:"var(--radius-lg)", padding:"var(--space-4)", display:"flex", flexDirection:"column", gap:"var(--space-3)" },
  header: { display:"flex", alignItems:"center", gap:"var(--space-2)" },
  icon:   { fontSize:"var(--text-lg)", color:"#d97706" },
  title:  { fontSize:"var(--text-sm)", fontWeight:"var(--weight-semibold)", color:"#92400e" },
  sub:    { fontSize:"var(--text-xs)", color:"#92400e", lineHeight:"var(--leading-normal)", margin:0 },
  row:    { padding:"var(--space-3)", background:"rgba(255,255,255,0.6)", borderRadius:"var(--radius-md)", display:"flex", flexDirection:"column", gap:"var(--space-2)" },
  desc:   { fontSize:"var(--text-sm)", color:"var(--color-text-primary)", margin:0 },
  cols:   { display:"flex", gap:"var(--space-3)", alignItems:"flex-start" },
  col:    { flex:1, display:"flex", flexDirection:"column", gap:2 },
  colDivider:{ fontSize:"var(--text-xs)", color:"var(--color-text-tertiary)", alignSelf:"center", flexShrink:0 },
  colLabel:  { fontSize:10, fontWeight:"var(--weight-semibold)", textTransform:"uppercase", letterSpacing:"0.06em", color:"var(--color-text-tertiary)", margin:0 },
  colVal:    { fontSize:"var(--text-sm)", color:"var(--color-text-primary)", background:"var(--color-bg-base)", borderRadius:"var(--radius-sm)", padding:"var(--space-1) var(--space-2)", margin:0 },
};


const pv = {
  overlay:        { position:"fixed", inset:0, background:"rgba(0,0,0,0.45)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:300, padding:"var(--space-6)" },
  box:            { background:"var(--color-surface)", borderRadius:"var(--radius-xl)", width:640, maxWidth:"100%", maxHeight:"80vh", display:"flex", flexDirection:"column", overflow:"hidden" },
  header:         { display:"flex", alignItems:"center", justifyContent:"space-between", padding:"var(--space-4) var(--space-5)", borderBottom:"1px solid var(--color-border)" },
  headerLeft:     { display:"flex", alignItems:"center", gap:"var(--space-3)", minWidth:0 },
  filename:       { fontSize:"var(--text-sm)", fontWeight:"var(--weight-medium)", color:"var(--color-text-primary)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" },
  closeBtn:       { background:"none", border:"none", fontSize:18, color:"var(--color-text-tertiary)", cursor:"pointer", padding:0, flexShrink:0 },
  body:           { flex:1, overflow:"auto", padding:"var(--space-5)" },
  hint:           { fontSize:"var(--text-sm)", color:"var(--color-text-secondary)" },
  error:          { fontSize:"var(--text-sm)", color:"var(--color-critical)" },
  emptyHint:      { fontSize:"var(--text-sm)", color:"var(--color-text-secondary)", lineHeight:"var(--leading-normal)", background:"var(--color-bg-subtle)", borderRadius:"var(--radius-md)", padding:"var(--space-4)" },
  text:           { fontSize:"var(--text-sm)", color:"var(--color-text-primary)", whiteSpace:"pre-wrap", wordBreak:"break-word", fontFamily:"inherit", lineHeight:"var(--leading-normal)", margin:0 },
  footer:         { display:"flex", justifyContent:"space-between", alignItems:"center", padding:"var(--space-4) var(--space-5)", borderTop:"1px solid var(--color-border)" },
  openOriginalBtn:{ fontSize:"var(--text-sm)", color:"var(--color-accent)", textDecoration:"none", fontWeight:"var(--weight-medium)" },
  closeFooterBtn: { fontSize:"var(--text-sm)", color:"var(--color-text-secondary)", background:"none", border:"1px solid var(--color-border)", borderRadius:"var(--radius-md)", padding:"var(--space-2) var(--space-4)", cursor:"pointer" },
};

// For the test only — the view is otherwise internal to Quick Start.
export { ProcessingView as ProcessingViewForTest };
